import 'focus-visible';
import DOMPurify from 'dompurify';
import create from '@verndale/core';
import modules from './modules';
import Rtf from './modules/rtf';
import {
  emailClick,
  phoneClick,
  detectScrollThresholds,
  detectSectionIsVisible
} from './helpers/helpers';
import {
  getStoredGeolocationData,
  getLocationByIpAddress,
  getGeolocationDataFromBrowserAndGoogle
} from './helpers/geolocation';
import { MaxmindResponse } from './helpers/maxmindResponseTypes';
import Cookies from 'cookies-ts';
import {
  QUIZ_RESULT_NAMES,
  getSummaryResults
} from './react-components/quiz-results/helpers';
import {
  secureStorage,
  USER_PERSONALIZATION_KEY
} from './helpers/global-storage';

export type DataLayerObject = {
  event?: string;
  event_action?: string;
  event_category?: string;
  event_label?: string;
  event_attributes?: { [key: string]: string | null | undefined | number };
  event_group?: string;
  distributor_type?: string;
  distance_filter?: string;
  event_failure?: string;
};
export type ExtendedWindowType = Window &
  typeof globalThis & {
    dataLayer: Array<DataLayerObject>;
    geoip2: {
      city: (
        onSuccess: CallableFunction,
        onError: CallableFunction
      ) => MaxmindResponse;
    };
    linksToHome: HTMLAnchorElement[];
    Engage: { settings: { pointOfSale: string }; personalize: () => void };
    engage: engageType;
  };

type personalizeDataType = {
  channel: string;
  currency: string;
  pointOfSale: string;
  friendlyId: string;
  params: {
    person_type?: string;
    zipCode?: string;
    body?: object;
  };
};

type navigationLinkType = { url: string; label: string; target: string };

type engageType = {
  personalize: (object: personalizeDataType) => {
    url?: string;
    cta?: { label: string; target: string; url: string };
    navigationLinks?: {
      url: string;
      label: string;
      target: string;
      navigationLinks?: navigationLinkType[];
    }[];
    codeRuleButton?: { label: string; target: string; url: string };
    html?: string;
    stormRuleButton?: { label: string; target: string; url: string };
  };
};

export const initRtf = () => {
  const rtfs = document.querySelectorAll<HTMLElement>('.rtf');

  rtfs?.forEach(rtf => {
    new Rtf(rtf);
  });
};

export const expireStorage = () => {
  const now = new Date();
  const userProfileExpiration = secureStorage.get('userProfileExpiration');
  const geolocationExpiration = secureStorage.get('geolocationExpiration');

  if (userProfileExpiration) {
    const userProfileExpirationDate = new Date(+userProfileExpiration);
    if (now > userProfileExpirationDate) {
      window.localStorage.removeItem('userProfile');
      window.localStorage.removeItem('userProfileExpiration');
    }
  }

  if (geolocationExpiration) {
    const geolocationExpirationDate = new Date(+geolocationExpiration);
    if (now > geolocationExpirationDate) {
      window.localStorage.removeItem('geolocation');
      window.localStorage.removeItem('geolocationExpiration');
    }
  }

  const quizzesResults = getSummaryResults();
  if (quizzesResults) {
    const { contractorResults, costEstimateResults, shingleResults } =
      quizzesResults;

    if (contractorResults?.expirationDate) {
      const contractorResultsExpirationDate = new Date(
        contractorResults.expirationDate
      );
      if (now > contractorResultsExpirationDate) {
        window.localStorage.removeItem(QUIZ_RESULT_NAMES.contractor);
      }
    }

    if (costEstimateResults?.expirationDate) {
      const costEstimateResultsExpirationDate = new Date(
        costEstimateResults.expirationDate
      );
      if (now > costEstimateResultsExpirationDate) {
        window.localStorage.removeItem(QUIZ_RESULT_NAMES.costEstimates);
      }
    }

    if (shingleResults?.expirationDate) {
      const shingleResultsExpirationDate = new Date(
        shingleResults.expirationDate
      );
      if (now > shingleResultsExpirationDate) {
        window.localStorage.removeItem(QUIZ_RESULT_NAMES.shingle);
      }
    }
  }
};

const isStorybook = !!document.getElementById('storybook-root');
const body = document.querySelector('body') as HTMLBodyElement;
const _window = window as ExtendedWindowType;

const pushSection = (e: IntersectionObserverEntry) => {
  const info = (e.target as HTMLElement).dataset.layerVisible;
  if (info) {
    const infoObj = JSON.parse(info);
    const prevData = _window?.dataLayer?.find(
      obj =>
        (obj as { event_category: string; event_action: string })
          .event_category === infoObj.event_category &&
        (obj as { event_category: string; event_action: string })
          .event_action === infoObj.event_action
    );
    if (!prevData) {
      _window.dataLayer.push(infoObj);
    }
  }
};

const setIphoneViewport = () => {
  if (navigator.userAgent.indexOf('iPhone') > -1) {
    document
      .querySelector('[name=viewport]')
      ?.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=1'
      );
  }
};

document.addEventListener(
  'DOMContentLoaded',
  async () => {
    if (!body.dataset.modulesLoaded || isStorybook) {
      expireStorage();
      create(modules).then(() => {
        body.dataset.modulesLoaded = 'true';
        setIphoneViewport();
        initRtf();
        window.addEventListener('click', e => pushDataLayer(e));
        detectScrollThresholds();
        detectSectionIsVisible(pushSection, '[data-layer-visible]');
        checkForPersona();
      });
    }
  },
  false
);

const pushDataLayer = (e: Event) => {
  const dataset =
    ((e.target as HTMLElement)?.closest('[data-layer]') as HTMLElement)
      ?.dataset || (e.target as HTMLElement)?.dataset;
  if (dataset.layer) {
    const datasetLayers = JSON.parse(dataset.layer as string);
    datasetLayers?.forEach((layer: { event: string }) => {
      if (dataset.oneTime) {
        const prevData = _window?.dataLayer?.find(
          obj => (obj as { event: string }).event === layer.event
        );
        if (!prevData) {
          _window.dataLayer.push(layer);
        }
      } else {
        _window.dataLayer.push(layer);
      }
    });
  }

  phoneClick(e);
  emailClick(e);
};

export const checkForPersona = () => {
  const interval = setInterval(checkForEngage, 1000);

  function checkForEngage() {
    if (_window.Engage) {
      const personalizeReadyEvent = new CustomEvent('personalizeReady', {});
      window.dispatchEvent(personalizeReadyEvent);
      clearInterval(interval);
      runFullStackExperience();
    }
  }
};

const runFullStackExperience = async () => {
  const personalizeMap = setPersonaLanguageMap();
  const personalizeLanguage = setPersonaLanguageMap(false);

  handleStormCodeRules();
  let redirect = false;
  let existingCookie = new Cookies().get(USER_PERSONALIZATION_KEY);

  const persona = document.querySelector('#persona') as HTMLDivElement;

  if (persona?.dataset?.personalize && !existingCookie) {
    existingCookie = persona?.dataset?.personalize;
    new Cookies().set(USER_PERSONALIZATION_KEY, existingCookie, {
      expires: 60 * 60 * 24 * 30
    });
  }

  const homePersona =
    (document.querySelector('#personalize-home') as HTMLDivElement)?.dataset
      .personalize || '';

  if (existingCookie) {
    if (homePersona === 'default') {
      redirect = true;
    } else {
      checkContractorCommercialRedirect(existingCookie);
      checkTerritoryManagerCommercial(existingCookie);
      checkDocumentLandingCommercial(existingCookie);
      productDetailContent(existingCookie);
    }
  }

  const linksToHome = Array.from(
    document.querySelectorAll<HTMLAnchorElement>('a[href="/"]')
  );

  (personalizeLanguage?.languages as [])?.forEach((lang: string) => {
    const languageLinks = Array.from(
      document.querySelectorAll<HTMLAnchorElement>(`a[href="/${lang}/"]`)
    );
    linksToHome.push(...languageLinks);
  });

  _window.linksToHome = linksToHome;

  if (personalizeMap?.homeUrl) {
    const personalizationData = {
      channel: 'WEB',
      currency: 'USD',
      pointOfSale: _window.Engage?.settings?.pointOfSale,
      friendlyId: personalizeMap.homeUrl as string,
      // optional attributes:
      params: {
        person_type: existingCookie ? existingCookie : ''
      }
    };

    const response = await (window as ExtendedWindowType).engage.personalize(
      personalizationData
    );

    if (response?.url) {
      if (!window.location.href.includes(response.url) && redirect) {
        window.location.href = response.url;
      } else {
        _window.linksToHome?.forEach(element =>
          element.setAttribute('href', response.url as string)
        );
      }
    }
  }
};

const setPersonaLanguageMap = (persona = true) => {
  const personalizeMapElement = document.querySelector(
    '[data-persona-mapping]'
  ) as HTMLElement;

  let personalizeMap: { [key: string]: string } = {};

  let personalizeLanguage: { [key: string]: [] | '' } = {};

  if (personalizeMapElement) {
    if (personalizeMapElement.dataset.personaMapping) {
      personalizeMap = JSON.parse(
        personalizeMapElement.dataset.personaMapping as string
      );
    }
    if (personalizeMapElement.dataset.languageMapping) {
      personalizeLanguage = JSON.parse(
        personalizeMapElement.dataset.languageMapping as string
      );
    }
  }
  if (persona) {
    return personalizeMap;
  }

  return personalizeLanguage;
};

export const handleChangePersona = async (persona: string) => {
  new Cookies().set(USER_PERSONALIZATION_KEY, persona, {
    expires: 60 * 60 * 24 * 30
  });

  const isHome =
    (document.querySelector('#personalize-home') as HTMLDivElement)?.dataset
      .personalize || '';

  const personalizeMap = setPersonaLanguageMap();

  if (personalizeMap?.homeUrl) {
    const personalizationData = {
      channel: 'WEB',
      currency: 'USD',
      pointOfSale: _window.Engage?.settings?.pointOfSale,
      friendlyId: personalizeMap.homeUrl as string,
      // optional attributes:
      params: {
        person_type: persona
      }
    };

    const response = await (window as ExtendedWindowType).engage.personalize(
      personalizationData
    );

    if (response?.url) {
      if (isHome !== '' && !window.location.href.includes(response.url)) {
        window.location.href = response.url;
      } else {
        _window.linksToHome?.forEach(element =>
          element.setAttribute('href', response.url as string)
        );
      }
    }
  }

  checkContractorCommercialRedirect(persona);
  checkTerritoryManagerCommercial(persona);
  checkDocumentLandingCommercial(persona);
  productDetailContent(persona);
};

const checkContractorCommercialRedirect = (persona: string) => {
  const contractorRedirect = (
    document.querySelector('#personalize-contractor') as HTMLDivElement
  )?.dataset.personalize;

  if (contractorRedirect) {
    const event = new CustomEvent('commercialChange', {
      detail: contractorRedirect.split(',').includes(persona)
    });
    window.dispatchEvent(event);
  }
};

const checkTerritoryManagerCommercial = (persona: string) => {
  const territoryRedirect = (
    document.querySelector('#personalize-territory') as HTMLDivElement
  )?.dataset.personalize;

  if (territoryRedirect) {
    const event = new CustomEvent('territoryChange', {
      detail: territoryRedirect.split(',').includes(persona)
    });

    window.dispatchEvent(event);
  }
};

const checkDocumentLandingCommercial = (persona: string) => {
  const documentRedirect = (
    document.querySelector('#personalize-document-landing') as HTMLDivElement
  )?.dataset.personalize;

  if (documentRedirect) {
    const event = new CustomEvent('documentLandingChange', {
      detail: documentRedirect.split(',').includes(persona)
    });
    window.dispatchEvent(event);
  }
};

let savedZip = '';

export const shingleDetailContent = async (zip: string, persona?: string) => {
  const personalizeMap = setPersonaLanguageMap();
  const personalizeLanguage = setPersonaLanguageMap(false);
  let _persona = '';

  if (zip) {
    savedZip = zip;
  }

  const savedPersona = new Cookies().get(USER_PERSONALIZATION_KEY);
  if (!persona && savedPersona) {
    _persona = savedPersona;
  } else if (persona) {
    _persona = persona;
  }
  const replaceContentShingle = document.querySelector(
    '#personalize-shingle-code-rule'
  ) as HTMLDivElement;

  if (replaceContentShingle && personalizeMap?.productShingleFull) {
    const personalizationData = {
      channel: 'WEB',
      currency: 'USD',
      pointOfSale: _window.Engage?.settings?.pointOfSale,
      friendlyId: personalizeMap.productShingleFull as string,
      // optional attributes:
      params: {
        zipCode: savedZip,
        persona: _persona,
        language: languageSelector(personalizeLanguage)
      }
    };

    const response = await (window as ExtendedWindowType).engage.personalize(
      personalizationData
    );

    const divToInsert = document.querySelector(
      (replaceContentShingle as HTMLElement).dataset.personalize as string
    );

    if (divToInsert) {
      if (response?.html) {
        divToInsert.innerHTML = DOMPurify.sanitize(response.html);
      } else {
        divToInsert.innerHTML = '';
      }
    }
  }
};

export const productDetailContent = async (persona: string) => {
  const personalizeMap = setPersonaLanguageMap();
  const personalizeLanguage = setPersonaLanguageMap(false);

  const replaceContentPdp = document.querySelector(
    '#personalize-pdp'
  ) as HTMLDivElement;

  if (replaceContentPdp && personalizeMap?.productDetailFull) {
    const personalizationData = {
      channel: 'WEB',
      currency: 'USD',
      pointOfSale: _window.Engage?.settings?.pointOfSale,
      friendlyId: personalizeMap.productDetailFull as string,
      // optional attributes:
      params: {
        zipCode: persona,
        language: languageSelector(personalizeLanguage)
      }
    };

    const response = await (window as ExtendedWindowType).engage.personalize(
      personalizationData
    );

    const divToInsert = document.querySelector(
      replaceContentPdp.dataset.personalize as string
    );

    if (divToInsert) {
      if (response?.html) {
        divToInsert.innerHTML = DOMPurify.sanitize(response.html);
      } else {
        divToInsert.innerHTML = '';
      }
    }
  }
};

const handleStormCodeRules = async () => {
  const data = getStoredGeolocationData();
  if (data) {
    stormRules(data.zip);
    codeRuleRedirect(data.zip);
  } else {
    const google = await getGeolocationDataFromBrowserAndGoogle();
    if (google) {
      stormRules(google.zip);
      codeRuleRedirect(google.zip);
    } else {
      const maxmind = await getLocationByIpAddress();
      if (maxmind) {
        stormRules(maxmind.zip);
        codeRuleRedirect(maxmind.zip);
      }
    }
  }
};

export const stormRules = async (zip: string) => {
  const storm = document.querySelector('#personalize-storm-rule');
  const personalizeMap = setPersonaLanguageMap();
  const personalizeLanguage = setPersonaLanguageMap(false);

  if (personalizeMap?.stormRule && storm) {
    const personalizationData = {
      channel: 'WEB',
      currency: 'USD',
      pointOfSale: _window.Engage?.settings?.pointOfSale,
      friendlyId: personalizeMap?.stormRule as string,
      // optional attributes:
      params: {
        zipCode: zip,
        language: languageSelector(personalizeLanguage)
      }
    };

    const response = await (window as ExtendedWindowType).engage.personalize(
      personalizationData
    );

    if (
      response.stormRuleButton &&
      !window.location.href.includes(response.stormRuleButton.url)
    ) {
      window.location.href = response.stormRuleButton.url as string;
    }
  }
};

export const codeRuleRedirect = async (zip: string) => {
  const personalizeMap = setPersonaLanguageMap();
  const personalizeLanguage = setPersonaLanguageMap(false);

  const codeRuleRedirect = document.querySelector(
    '#personalize-code-rule-redirect'
  );

  if (personalizeMap?.codeRuleRedirect && codeRuleRedirect) {
    const personalizationData = {
      channel: 'WEB',
      currency: 'USD',
      pointOfSale: _window.Engage?.settings?.pointOfSale,
      friendlyId: personalizeMap.codeRuleRedirect as string,
      // optional attributes:
      params: {
        zipCode: zip,
        language: languageSelector(personalizeLanguage)
      }
    };

    const response = await (window as ExtendedWindowType).engage.personalize(
      personalizationData
    );

    if (
      response?.codeRuleButton &&
      !window.location.href.includes(response.codeRuleButton.url)
    ) {
      window.location.href = response.codeRuleButton.url as string;
    }
  }
};

const languageSelector = (langMapping: {
  [key: string]: string[] | string;
}) => {
  const language: string = window.location.pathname.split('/')[1];

  if (langMapping?.languages?.includes(language)) {
    return language;
  } else {
    return langMapping.default;
  }
};

export const codeRule = async (zip: string) => {
  const personalizeMap = setPersonaLanguageMap();
  const personalizeLanguage = setPersonaLanguageMap(false);

  if (personalizeMap?.codeRule) {
    const personalizationData = {
      channel: 'WEB',
      currency: 'USD',
      pointOfSale: _window.Engage?.settings?.pointOfSale,
      friendlyId: personalizeMap.codeRule as string,
      // optional attributes:
      params: {
        zipCode: zip,
        language: languageSelector(personalizeLanguage)
      }
    };

    const response = await (window as ExtendedWindowType).engage.personalize(
      personalizationData
    );

    return response;
  }
  return;
};

export const isAnyKeyEmpty = (obj: object) => {
  for (const key in obj) {
    if (!obj[key as keyof object]) {
      return true;
    }
  }
  return false;
};

export const personalizeAddressData = async (_body: object) => {
  const personalizeMap = setPersonaLanguageMap();

  if (personalizeMap) {
    const personalizationData = {
      channel: 'WEB',
      currency: 'USD',
      pointOfSale: _window.Engage?.settings?.pointOfSale,
      friendlyId: personalizeMap.addressData as string,
      // optional attributes:
      params: {
        body: _body
      }
    };

    await (window as ExtendedWindowType).engage.personalize(
      personalizationData
    );
  }
};
